import { memberKey, focusStyle } from "../../lib/team";

const DARK_TAPES = new Set(["bg-cobalt", "bg-grass", "bg-tangerine", "bg-scarlet", "bg-ink", "bg-forest"]);

const initials = (name) => name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const scrollToMember = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history?.replaceState) window.history.replaceState(null, "", `#${id}`);
};

const Avatar = ({ person, group }) => {
  const key = memberKey(person.name);
  const id = group === "founder" ? "founder" : `member-${key}`;
  const first = person.name.split(" ")[0];
  return (
    <button
      type="button"
      onClick={() => scrollToMember(id)}
      data-testid={`team-glance-${key}`}
      aria-label={`Jump to ${person.name}, ${person.role}`}
      className="group flex w-[76px] shrink-0 snap-start flex-col items-center text-center sm:w-[88px]"
    >
      <span className="relative block h-14 w-14 overflow-hidden rounded-full ring-2 ring-bone shadow-[0_6px_16px_-8px_rgba(26,23,20,0.45)] transition-transform duration-300 ease-out group-hover:-translate-y-1 group-hover:scale-105 sm:h-16 sm:w-16">
        {person.photoNow ? (
          <img src={person.photoNow} alt="" className="h-full w-full object-cover" style={focusStyle(person)} loading="lazy" />
        ) : (
          <span className={`flex h-full w-full items-center justify-center font-serif text-lg italic ${person.tape || "bg-beige"} ${DARK_TAPES.has(person.tape) ? "text-bone" : "text-ink"}`}>
            {initials(person.name)}
          </span>
        )}
      </span>
      <span className="mt-2 block w-full truncate text-[12px] font-semibold leading-tight text-ink sm:text-[13px]">{first}</span>
      <span className="mt-0.5 block text-[10px] uppercase tracking-[0.14em] text-olive/80">
        {group === "founder" ? "founder" : group === "board" ? "board" : "advisor"}
      </span>
    </button>
  );
};

/**
 * TeamGlance — the whole team in one scrollable row under the page header so
 * the board is visible above the fold; each avatar jumps to that person's card.
 */
export const TeamGlance = ({ founder, board, advisors }) => (
  <nav aria-label="Team at a glance" data-testid="team-glance" className="mt-10 lg:mt-12">
    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-olive">At a glance · tap a face to jump</p>
    <div className="-mx-6 mt-4 flex snap-x gap-4 overflow-x-auto px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:gap-5 sm:overflow-visible sm:px-0">
      <Avatar person={founder} group="founder" />
      <span aria-hidden="true" className="my-3 hidden w-px shrink-0 self-stretch bg-ink/15 sm:block" />
      {board.map((p) => <Avatar key={p.name} person={p} group="board" />)}
      <span aria-hidden="true" className="my-3 hidden w-px shrink-0 self-stretch bg-ink/15 sm:block" />
      {advisors.map((p) => <Avatar key={p.name} person={p} group="advisor" />)}
    </div>
  </nav>
);
